import {Locale} from "./commonTypes";

export enum RecipeType {
    RECIPE_TYPE_KASHA = 'kasha',
    RECIPE_TYPE_SNACK = 'snack',
    RECIPE_TYPE_BOWL = 'bowl',
    RECIPE_TYPE_SOUP = 'soup',
    RECIPE_TYPE_BREAD = 'bread',
    RECIPE_TYPE_SMOOTHIES = 'smoothies',
    RECIPE_TYPE_GRANOLA = 'granola',
    RECIPE_TYPE_SALAD = 'salad',
    RECIPE_TYPE_OMELETTE = 'omelette',
}

export enum RecipeSource {
    RECIPE_CHARACTERISTIC_SOURCE_CHAPCHE = "source-chapche",
    RECIPE_CHARACTERISTIC_SOURCE_VKUSVILL = "source-vkusvill",
}

export enum RecipeTaste {
    RECIPE_CHARACTERISTIC_TASTE_SWEET = "taste-sweet",
    RECIPE_CHARACTERISTIC_TASTE_SALTY = "taste-salty",
    RECIPE_CHARACTERISTIC_TASTE_SOUR = "taste-sour",
    RECIPE_CHARACTERISTIC_TASTE_SPICY = "taste-spicy",
    RECIPE_CHARACTERISTIC_TASTE_BITTER = "taste-bitter",
    RECIPE_CHARACTERISTIC_TASTE_UMAMI = "taste-umami",
}

export enum HeatTreatment {
    RAW = "raw",
    BOILED = "boiled",
    STEAMED = "steamed",
    BAKED = "baked",
    ROASTED = "roasted",
    BROILED = "broiled",
    FRIED = "fried",
    SAUTEED = "sauteed",
    STEWED = "stewed",
    POACHED = "poached",
    MICROWAVED = "microwaved",
    DRIED = "dried",
    SOUS_VIDE = "sous-vide",
    PRESSURE_COOKED = "pressure-cooked",
}

enum FormType {
    PRODUCT_PART = "product-part",
}

enum FormGroup {
    GRAIN = "grain",
    NUT = "nut",
    BERRY = "berry",
    VEGETABLE = "vegetable",
    FRUIT = "fruit",
    ANY = "any",
    MEAT = "meat",
    FISH = "fish",
    POULTRY = "poultry",
    DAIRY = "dairy",
    LEGUMES = "legumes",
    SALAD_GREEN = "salad-green",
    EGG = "egg",
    CHEESE = "cheese",
    PASTA = "pasta",
    OIL = "oil",
    SPICES = "spices",
    SAUCE = "sauce",
    VINEGAR = "vinegar",
    HONEY = "honey",
    SYRUP = "syrup",
    OIL_SEEDS = "oil-seeds",
    MUSHROOM = "mushroom",
    BROTH = "broth",
    WATER = "water",
    LIQUID = "liquid",
}

export enum IngredientType {
    PRODUCT = 'product',
    CEREAL_MIX =  'cereal-mix',
}

enum IngredientGroup {
    RECIPE_INGREDIENT_GROUP_BASE = "base",
    RECIPE_INGREDIENT_GROUP_ADDS = "adds",
    RECIPE_INGREDIENT_GROUP_TASTY = "tasty",
    RECIPE_INGREDIENT_GROUP_LIQUID = "liquid",
}

type IngredientForm = {
    formCode?: string;
    formType?: FormType;
    formGroup?: FormGroup;
};

type IngredientPart = {
    readonly id?: string;
    productCode?: string;
    heatTreatment?: HeatTreatment | string | null;
    ingredientForms?: IngredientForm[];
};

type Ingredient = {
    readonly id?: string;
    ingredientGroup?: IngredientGroup;
    ingredientType?: IngredientType;
    unitWeight?: number;
    amountKind?: 'teaspoon' | 'pinch' | 'to_taste' | 'gram' | string;
    amountValue?: number | null;
    ingredientParts?: IngredientPart[];
};

export enum RecipeCharacteristicCategory {
    RECIPE_CHARACTERISTIC_TASTE = "taste",
    RECIPE_CHARACTERISTIC_SOURCE = "source",
    RECIPE_CHARACTERISTIC_PRODUCT_CATEGORY = "product-category",
}

type RecipeCharacteristic = {
    category: RecipeCharacteristicCategory;
    value: RecipeTaste | RecipeSource;
};

type RecipeName = {
    locale: Locale;
    name: string;
};

export enum KashaConsistency {
    FLUFFY = 'fluffy',
    CREAMY = 'creamy',
    STICKY = 'sticky',
}

export type Recipe = {
    readonly id?: string;
    recipeType?: string;
    takeAsIs: boolean;
    baseMin: number;
    baseMax: number;
    addsMin: number;
    addsMax: number;
    tastyMin: number;
    tastyMax: number;
    baseShare: number;
    addsShare: number;
    defaultKashaConsistency?: string | null;
    dateCreated?: string;
    characteristics?: RecipeCharacteristic[];
    ingredients?: Ingredient[];
    names?: RecipeName[];
};

export const buildDefaultRecipe = (): Recipe => {
    return {
        takeAsIs: false,
        baseMin: 0,
        baseMax: 0,
        addsMin: 0,
        addsMax: 0,
        tastyMin: 0,
        tastyMax: 0,
        baseShare: 70,
        addsShare: 30,
    };
};

export enum CookingStepConditionType {
    PRODUCT_FIELD = 'product_field',
    PRODUCT_CODE = 'product_code',
    ANY_INGREDIENT_GROUP_PRESENT = 'any_ingredient_group_present',
    KASHA_CONSISTENCY = 'kasha_consistency',
    KASHA_CONSISTENCY_IN = 'kasha_consistency_in',
}

export enum ReminderKind {
    PRESET = 'preset',
    OFFSET_HOURS = 'offset_hours',
}

export enum ReminderPreset {
    EVENING_BEFORE = 'evening_before',
    MORNING_OF = 'morning_of',
}

export enum MeasureBlock {
    NONE = 'none',
    GRAIN = 'grain',
    LIQUID = 'liquid',
    BOTH = 'both',
}

export type CookingStepText = {
    locale: Locale | string;
    text: string;
};

export type CookingStepCondition = {
    id?: string;
    conditionType: CookingStepConditionType | string;
    ingredientGroup?: string;
    fieldName?: string;
    operator?: string;
    value?: string;
    productCode?: string;
};

export type CookingStep = {
    id?: string;
    recipeType?: string;
    stepOrder: number;
    stepCode: string;
    measureBlock?: MeasureBlock | string;
    isReminder?: boolean;
    reminderKind?: ReminderKind | string | null;
    reminderPreset?: ReminderPreset | string | null;
    reminderOffsetHours?: number | null;
    texts: CookingStepText[];
    conditions: CookingStepCondition[];
};

export type CookingStepWrite = {
    stepOrder: number;
    stepCode: string;
    measureBlock: MeasureBlock | string;
    isReminder: boolean;
    reminderKind?: ReminderKind | string | null;
    reminderPreset?: ReminderPreset | string | null;
    reminderOffsetHours?: number | null;
    texts: CookingStepText[];
    conditions: CookingStepCondition[];
};
